import { t } from '@/lib/i18n';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Result, message } from 'antd';
import { AuthLayout } from '@/components/auth-layout';
import { ResetPasswordForm } from '@/features/auth/password/reset-form';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Link } from '@tanstack/react-router';
import { Button } from 'antd';

/**
 * Where the reset link lands: `/reset-password?token=…`.
 *
 * The token is read straight from the query string and passed on untouched; the API is the
 * only thing that can judge it. Two shapes are accepted on purpose: better-auth appends
 * `?token=` to the `redirectTo` it was given, and its callback route can also arrive with
 * `?error=INVALID_TOKEN` — both end up here, so both are handled instead of showing an empty
 * form to someone whose link was merely expired.
 */
export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: stringOrUndefined(search.token),
    error: stringOrUndefined(search.error),
  }),
  component: ResetPasswordPage,
});

const stringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

function ResetPasswordPage() {
  useDocumentTitle(t('重置密码'));
  const { token, error } = Route.useSearch();
  const navigate = useNavigate();

  if (token === undefined || error !== undefined) {
    return (
      <AuthLayout title={t('重置密码')} subtitle={t('链接无法直接使用')}>
        <Result
          status="warning"
          title={t('链接不完整或已失效')}
          subTitle={
            error === undefined
              ? t(
                  '这个地址里没有重置令牌，通常是邮件客户端把链接截断了。请从邮件里重新打开，或申请一封新的重置邮件。',
                )
              : t('链接里的令牌无效。令牌只能使用一次，且会过期。')
          }
          extra={
            <Link to="/forgot-password" search={{ redirect: '/' }}>
              <Button type="primary">{t('重新申请重置邮件')}</Button>
            </Link>
          }
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('重置密码')}
      subtitle={t('设置一个新密码，然后回到登录页')}
    >
      <ResetPasswordForm
        token={token}
        onReset={async () => {
          message.success(t('密码已重置，请使用新密码登录'));
          await navigate({ to: '/login', search: { redirect: '/' } });
        }}
      />
    </AuthLayout>
  );
}
